import { useTranslation } from 'react-i18next';
import {
    AlertTriangle,
    FileText,
    Info,
    LayoutGrid,
    MapPin,
} from 'lucide-react';
import { Card } from '../ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import { CategoryChip } from '../ui-components/category/CategoryChip';
import { MRIMachinePopulated } from './types';
import { parseCoils } from './utils';
import { roomPolicies } from './roomOverviewUtils';
import FavoriteButton from './FavoriteButton';
import CoilChip from './CoilChip';
import { FieldStrengthTile } from './FieldStrengthChip';

type RoomCardData = Pick<
    MRIMachinePopulated,
    '_id' | 'Name' | 'Model' | 'Manufacturer' | 'FieldStrength'
> &
    Partial<
        Pick<
            MRIMachinePopulated,
            'TRCoils' | 'location' | 'category' | 'equipment' | 'policies'
        >
    >;

interface RoomCardProps {
    machine: RoomCardData;
    isFavorited?: boolean;
    onFavoriteChange?: (machineId: string, isFavorited: boolean) => void;
    onClick?: (machineId: string) => void;
    restrictionText?: string;
    showLocation?: boolean;
}

export default function RoomCard({
    machine,
    isFavorited = false,
    onFavoriteChange,
    onClick,
    restrictionText,
    showLocation = false,
}: RoomCardProps) {
    const { t } = useTranslation();

    const coils = parseCoils(machine.TRCoils);
    const itemCount = machine.equipment?.length ?? 0;
    const policyCount = roomPolicies(machine).length;

    return (
        <Card
            className="flex flex-col gap-3 p-4 cursor-pointer transition-colors border-subtle hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onClick?.(machine._id)}
            onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.(machine._id);
                }
            }}
            tabIndex={0}
            role="button"
            aria-label={machine.Name}
        >
            <div className="flex items-center gap-3">
                <FieldStrengthTile fieldStrength={machine.FieldStrength} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate text-base font-semibold tracking-tight">
                        {machine.Name}
                    </span>
                    {(machine.category ||
                        (showLocation && machine.location)) && (
                        <div className="flex min-w-0 items-center gap-3 text-xs font-medium text-subtle-foreground">
                            {machine.category && (
                                <CategoryChip category={machine.category} />
                            )}
                            {showLocation && machine.location && (
                                <span className="flex min-w-0 items-center gap-1.5">
                                    <MapPin
                                        className="h-3 w-3 shrink-0"
                                        aria-hidden="true"
                                    />
                                    <span className="truncate">
                                        {machine.location}
                                    </span>
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <FavoriteButton
                    machineId={machine._id}
                    machineName={machine.Name}
                    isFavorited={isFavorited}
                    onFavoriteChange={onFavoriteChange}
                />
            </div>
            <div className="border-t border-subtle" />
            {restrictionText && (
                <div className="flex items-start gap-1.5 text-xs text-warning rounded-md bg-warning/10 border border-warning/25 px-2 py-1">
                    <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                    <span>{restrictionText}</span>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger
                                asChild
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Info className="h-4 w-4 shrink-0 mt-0.5 text-subtle-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                className="max-w-xs text-xs"
                            >
                                {t(
                                    'mriCompatibility.detail.gradientExplanation'
                                )}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            )}
            <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-baseline gap-x-3 gap-y-2 text-sm">
                <span className="font-medium text-subtle-foreground">
                    {t('roomOverview.machineCard.scanner')}
                </span>
                <span className="truncate">
                    {machine.Manufacturer && (
                        <span>{machine.Manufacturer} </span>
                    )}
                    <span className="font-medium">{machine.Model}</span>
                </span>
                <span className="font-medium text-subtle-foreground">
                    {t('roomOverview.machineCard.coils')}
                </span>
                {coils.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {coils.map((coil, i) => (
                            <CoilChip key={i} coil={coil} />
                        ))}
                    </div>
                ) : (
                    <span className="text-xs font-medium text-subtle-foreground">
                        {t('roomOverview.machineCard.noCoils')}
                    </span>
                )}
            </div>
            <div className="mt-auto flex items-center gap-1.5 border-t border-subtle pt-3 text-xs font-medium text-subtle-foreground">
                {itemCount > 0 && (
                    <span className="flex items-center gap-1">
                        <LayoutGrid className="h-3 w-3" aria-hidden="true" />
                        {t('roomOverview.machineCard.itemCount', {
                            count: itemCount,
                        })}
                    </span>
                )}
                {itemCount > 0 && policyCount > 0 && (
                    <span aria-hidden="true">·</span>
                )}
                {policyCount > 0 && (
                    <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" aria-hidden="true" />
                        {t('roomOverview.machineCard.policyCount', {
                            count: policyCount,
                        })}
                    </span>
                )}
            </div>
        </Card>
    );
}
